<!-- BaseSearchForm provides shared search functionality and structure for Packages and Hotels. -->
<script setup lang="ts">
import { computed, ref, provide, watch, inject, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { onClickOutside } from "@vueuse/core";
import { useQueryStore } from "../../stores/query";
import { useAnalyticsTimer } from "../../composables/useAnalyticsTimer";
import { useAnalyticsAbandonment } from "../../composables/useAnalyticsAbandonment";
import DatePicker from "../DatePicker/DatePicker.vue";
import LocationField from "../LocationField/LocationField.vue";
import {
  VacvDropDown,
  VacvGuestsInputField,
  VacvCubaPopup,
  Option,
  AeroplanOption,
  GuestsData,
  USER_GROUP,
} from "vacv-components";
import SearchPreview from "../SearchPreview/SearchPreview.vue";

import {
  dataLayerSearchWidgetError,
  dataLayerSearchWidgetSuggestion,
  dataLayerSearchWidgetSearch,
} from "../../analytics/package-search-form";

import { SearchError } from "../../../types/analytics";
import {
  DateRangeData,
  LocationData,
  SearchFormData,
} from "../../../types/form-data";
import type { ProductType, PlayerName } from "../../../types/common";
import { TabConfig } from "../../../types/tabs";
import { ModifySearchMode, FlightTrip } from "../../../types/common";
import { CABIN_CLASS_TYPES } from "../../utils/cabinClass";
import { getEnvVar } from "../../utils/env";

export interface Props {
  tabConfig: TabConfig;
  productType: ProductType;
  playerName?: PlayerName;
  formData: SearchFormData;
  isPackages?: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(["modify", "show-cuba-popup", "submit"]);

const showInfantSelector =
  getEnvVar("VITE_GUESTS_INPUT_ALLOW_INFANTS") === "true";

const activeTab = inject("activeTab", ref("vacation_packages_tab")); // fallback value
const audience = inject("audience", USER_GROUP.GUEST);
const containerRef = inject("containerRef", ref(null));
const searchFormMode = inject("searchFormMode", ref(ModifySearchMode.view));

const { t, locale } = useI18n({ useScope: "global" });
const queryStore = useQueryStore(props.tabConfig.id!);

const { clearAnalyticsTimeout, setAnalyticsTimeout, sendAnalyticsAbandonment } =
  useAnalyticsAbandonment(props.tabConfig.id!);
const { handleFirstClickForAnalytics, handleSubmitClickForAnalytics } =
  useAnalyticsTimer();

const showCubaPopup = ref(false);

const isActiveTab = computed(() => activeTab.value === props.tabConfig.id);
const isModifySearch = computed(
  () =>
    props.tabConfig.isModifySearch &&
    searchFormMode.value === ModifySearchMode.view
);
// Indicates that the tabConfig has .forModifySearch set to true and the modify button has been clicked
const isUpdating = computed(
  () =>
    props.tabConfig.isModifySearch &&
    searchFormMode.value === ModifySearchMode.update
);

const cabinClassOptions = computed(() =>
  Array.from(CABIN_CLASS_TYPES).map((type) => ({
    label: t("form.cabinClasses." + type),
    value: type,
  }))
);

const guests = ref(props.formData.guests);

const syncWithQueryStore = () => {
  if (props.formData.departureEntity) {
    queryStore.setLocation(
      props.formData.departureEntity as LocationData,
      "departure"
    );
  }

  if (props.formData.arrivalEntity) {
    queryStore.setLocation(
      props.formData.arrivalEntity as LocationData,
      "arrival"
    );
  }

  if (!queryStore.query.guests) {
    queryStore.setGuests(props.formData.guests);
    guests.value = props.formData.guests;
  }

  if (props.formData.initialCabinClass) {
    queryStore.setCabinClass(props.formData.initialCabinClass);
  } else if (props.isPackages && !queryStore.query.cabinClass) {
    queryStore.setCabinClass("economy");
  }
  queryStore.setLanguage(locale.value);
};

const handleDepartureSelection = (value: LocationData) => {
  queryStore.setLocation(value, "departure");
};

const handleArrivalSelection = (value: LocationData) => {
  queryStore.setLocation(value, "arrival");
  if (value && queryStore.query.departureLocation?.code) {
    dataLayerSearchWidgetSuggestion(
      value,
      queryStore.query.departureLocation?.code
    );
  }
};

const handleDatesSelection = (value: DateRangeData) => {
  queryStore.setDates(value);
};
const handleGuestsSelection = (value: GuestsData) => {
  queryStore.setGuests(value);
};

const handleCabinClassSelection = (
  _oldValue?: Option | AeroplanOption | null,
  newValue?: Option | AeroplanOption | null
) => {
  queryStore.setCabinClass(newValue?.value?.toString() || "");
};

const handleFieldClick = () => {
  handleFirstClickForAnalytics();
  setAnalyticsTimeout();
};

const handleClickOutside = () => {
  if (isActiveTab.value) {
    sendAnalyticsAbandonment();
  }
};

const handleGuestsError = (error: SearchError) => {
  dataLayerSearchWidgetError(error);
};

const shouldShowCubaPopup = () =>
  queryStore.query.arrivalLocation?.cubaDestination === true;

const handleFormSubmit = async () => {
  if (queryStore.isDataValid) {
    if (shouldShowCubaPopup()) {
      // Show Cuba popup, submitSearch will be triggered on continue
      showCubaPopup.value = true;
      emit("show-cuba-popup");
    } else {
      submitSearch();
    }
  }
};

const submitSearch = async () => {
  handleSubmitClickForAnalytics();
  clearAnalyticsTimeout();
  dataLayerSearchWidgetSearch(props.productType);
  emit("submit");
};

const onCubaCancel = () => {
  showCubaPopup.value = false;
};
const onCubaContinue = async () => {
  showCubaPopup.value = false;
  await submitSearch();
};

onMounted(() => {
  syncWithQueryStore();
  onClickOutside(containerRef.value, handleClickOutside);
});

watch(
  () => activeTab.value,
  (newTab) => {
    if (newTab === props.tabConfig.id) {
      setAnalyticsTimeout();
    } else {
      clearAnalyticsTimeout();
    }
  }
);

const flightTrip = computed((): FlightTrip | null => {
  const departure = queryStore.query.departureLocation;
  const arrival = queryStore.query.arrivalLocation;
  if (departure?.code && arrival?.ttsDestinationCode) {
    return {
      origin: departure.code.replace(/_/g, ","),
      destination: arrival.ttsDestinationCode.replace(/_/g, ","),
    } as FlightTrip;
  } else {
    return null;
  }
});

provide("handleFirstClickForAnalytics", handleFirstClickForAnalytics);
provide("setAnalyticsTimeout", setAnalyticsTimeout);
</script>
<template>
  <FormKit
    :id="'search-form-' + tabConfig.id"
    type="form"
    :config="{ validationVisibility: 'submit' }"
    :actions="false"
    @submit="handleFormSubmit"
  >
    <div v-if="isModifySearch">
      <search-preview
        :searchType="tabConfig.searchType"
        :departureLocationName="formData.departureEntity?.name"
        :arrivalLocationName="formData.arrivalEntity?.name"
        :departure-date="formData.departureDateObject"
        :return-date="formData.returnDateObject"
        :number-of-guests="formData.totalGuests"
        :number-of-rooms="formData.guests.totalRooms"
        :number-of-nights="formData.numberOfNights"
        @modify="emit('modify')"
      />
    </div>
    <div
      v-else
      class="vacv-form-row vacv-input wide-5-items"
      :class="tabConfig.searchType"
    >
      <div v-if="isPackages" class="vacv-form-item flight departure">
        <location-field
          :player-name="playerName"
          :prefill-location="formData.departureEntity"
          field-name="from"
          :label-name="$t('form.departure.label')"
          :placeholder-name="$t('form.departure.placeholder')"
          :tooltip-message="$t('form.departure.info')"
          :origin="true"
          :geolocateAirport="!formData.departureEntity"
          :locale="locale"
          :tabId="tabConfig.id"
          @selectItem="handleDepartureSelection"
        />
      </div>
      <div class="vacv-form-item flight arrival">
        <location-field
          :player-name="playerName"
          :prefill-location="formData.arrivalEntity"
          field-name="to"
          :label-name="$t('form.destination.label')"
          :placeholder-name="$t('form.destination.placeholder')"
          :tooltip-message="$t('form.destination.info')"
          :origin="false"
          :geolocateAirport="false"
          :locale="locale"
          :tabId="tabConfig.id"
          @selectItem="handleArrivalSelection"
        />
      </div>
      <div class="vacv-form-item dates">
        <date-picker
          :min-stay="tabConfig.minStay"
          :search-type="tabConfig.searchType"
          @selectDates="handleDatesSelection"
          @click="handleFieldClick"
          :audience="audience"
          :min-date="tabConfig.minDate"
          :max-date="tabConfig.maxDate"
          :default-departure-date="formData.departureDate"
          :default-return-date="formData.returnDate"
          :tabId="tabConfig.id"
          :flightTrip="flightTrip"
        />
      </div>
      <div class="vacv-form-item guests">
        <VacvGuestsInputField
          v-model="guests"
          :showInfantSelector="showInfantSelector"
          :tabId="tabConfig.id"
          @analyticsErrorMessage="handleGuestsError"
          @submit="handleGuestsSelection"
          @click="handleFieldClick"
        />
      </div>
      <div class="vacv-form-item submit">
        <button type="submit" class="vacv-btn-primary">
          {{ $t(isUpdating ? "form.update" : "form.submit") }}
        </button>
      </div>
      <VacvDropDown
        v-if="isPackages"
        ref="cabinClassDropdownRef"
        class="cabinclass"
        :dropDownId="tabConfig.id + '-cabinclass'"
        :initialValue="formData.initialCabinClass"
        :options="cabinClassOptions"
        openLeft
        @value-change="handleCabinClassSelection"
        @click="handleFieldClick"
      />
    </div>
  </FormKit>
  <vacv-cuba-popup
    :show="showCubaPopup"
    @cancel="onCubaCancel"
    @continue="onCubaContinue"
  />
</template>
<style lang="scss">
@use "vacv-components/themes/acv-default/default-includes" as *;

.spa-overlay {
  z-index: 1032; // layer over mobile sticky summary which has z-index 1031
  display: block;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: $col-blacksemi;
  overflow: hidden;
}
</style>
